/**
 * API Client — configured sentryFetch for Midway-authenticated API calls,
 * wrapped with a request dedup + short-TTL cache layer.
 *
 */

import { configure } from '@amzn/sentry-fetch';

const sentryFetch = configure({
    followMidwayStepUp: true,
    followMidwayStepUpOptions: { mode: 'modal' },
});

/**
 * Pick the transport for a request.
 *
 * The corp site (eligibility.*.picking.aft.amazon.dev) authenticates via Midway,
 * so it uses sentry-fetch (which transparently completes the Midway SSO step-up).
 *
 * The AFT-X externalized site (*.aft.amazonoperations.app) has NO Midway: the
 * AFT-X/FCMenu secure cookie authenticates at the API Gateway RequestAuthorizer.
 * Using sentry-fetch there triggers a Midway step-up that navigates to
 * `/sso/login`, which CloudFront answers with the SPA index.html (304) and loops
 * forever. On that domain use a plain credentialed fetch so the AFT-X cookie is
 * sent and the authorizer can validate it.
 */
function baseFetch(url: string, init?: RequestInit): Promise<Response> {
    if (typeof window !== 'undefined' &&
        window.location.host.endsWith('.aft.amazonoperations.app')) {
        return window.fetch(url, { credentials: 'include', ...init });
    }
    return sentryFetch(url, init);
}


const GET_CACHE_TTL_MS = 30_000;

interface CacheEntry {
    /** A never-read response kept solely to hand out fresh clones. */
    response: Response;
    expiresAt: number;
}

/** Completed-response cache (GET only), keyed by `method URL`. */
const responseCache = new Map<string, CacheEntry>();
/** In-flight GET promises, keyed by `method URL`, for concurrent dedup. */
const inFlight = new Map<string, Promise<Response>>();
/**
 * Monotonic cache "generation", bumped on every clear. A GET captures the
 * generation when it is issued and only writes its result if the generation is
 * unchanged on resolve. This closes a race where a GET already in flight when a
 * mutation fires would otherwise land afterwards and re-populate the cache with
 * pre-mutation (stale) data, silently undoing the invalidation.
 */
let cacheGeneration = 0;

function cacheKey(url: string, method: string): string {
    return `${method} ${url}`;
}

/**
 * Clear all cached/in-flight GET state. Called on mutations and on
 * `warehouseChanged` so stale warehouse-scoped data is never served.
 */
export function clearApiCache(): void {
    cacheGeneration++;
    responseCache.clear();
    inFlight.clear();
}

// Bust the cache when the selected warehouse changes (dispatched by
// WarehouseSelector and WarehouseUrlHandler). Guarded for non-browser/test envs.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('warehouseChanged', clearApiCache);
}

/**
 * Drop-in replacement for the configured sentry-fetch with dedup + short-TTL
 * caching for idempotent GETs. Non-GET requests pass through and invalidate the
 * cache on success.
 */
async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
    const method = (init?.method ?? 'GET').toUpperCase();

    // Only GETs are safe to dedup/cache. A successful mutation invalidates the
    // cache so the next read reflects the change.
    if (method !== 'GET') {
        const res = await baseFetch(url, init);
        if (res.ok) {
            clearApiCache();
        }
        return res;
    }

    const key = cacheKey(url, method);
    const now = Date.now();

    // 1. Fresh cached response — return a clone (the stored copy is never read).
    const entry = responseCache.get(key);
    if (entry && entry.expiresAt > now) {
        return entry.response.clone();
    }
    if (entry) {
        responseCache.delete(key);
    }

    // 2. A matching request is already in flight — await and clone it.
    const pending = inFlight.get(key);
    if (pending) {
        const res = await pending;
        return res.clone();
    }

    // 3. Issue the network request; cache the result on success.
    // Capture the generation now; if a clear (mutation / warehouseChanged) happens
    // while this GET is in flight, the generation will differ on resolve and we
    // skip the write so stale pre-mutation data is never re-cached.
    const genAtStart = cacheGeneration;
    const promise = baseFetch(url, init).then((res: Response) => {
        if (res.ok && cacheGeneration === genAtStart) {
            responseCache.set(key, {
                response: res.clone(),
                expiresAt: Date.now() + GET_CACHE_TTL_MS,
            });
        }
        return res;
    });
    inFlight.set(key, promise);

    try {
        const res = await promise;
        return res.clone();
    } finally {
        inFlight.delete(key);
    }
}

export { apiFetch };
